import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { escolarLines, escolarProducts } from '@/mocks/escolar';
import { useSEO } from '@/hooks/useSEO';

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`bg-white rounded-2xl border transition-all duration-200 ${open ? 'border-[#E8610A]/30' : 'border-gray-100'}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer group"
      >
        <span className={`font-semibold text-sm pr-4 leading-snug transition-colors ${open ? 'text-[#E8610A]' : 'text-gray-800 group-hover:text-[#E8610A]'}`}>{question}</span>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${open ? 'bg-[#E8610A] text-white rotate-180' : 'bg-[#E8610A]/10 text-[#E8610A]'}`}>
          <i className="ri-arrow-down-s-line text-base"></i>
        </div>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <div className="border-t border-gray-100 pt-4">
            <p className="text-gray-600 text-sm leading-relaxed">{answer}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const segments = [
  { icon: 'ri-school-line', label: 'Escolas Públicas e Privadas' },
  { icon: 'ri-building-line', label: 'Universidades' },
  { icon: 'ri-bear-smile-line', label: 'Creches e Pré-escolas' },
  { icon: 'ri-community-line', label: 'Centros Educacionais' },
  { icon: 'ri-government-line', label: 'Órgãos Públicos' },
  { icon: 'ri-book-2-line', label: 'Cursos e Institutos' },
];

export default function EscolarPage() {
  const [activeLine, setActiveLine] = useState('conjunto-aluno');
  const [selectedProduct, setSelectedProduct] = useState<typeof escolarProducts[0] | null>(null);

  useSEO({
    title: 'Mobiliário Escolar Caruaru PE – Da Creche à Universidade | Home Office Premium',
    description: 'Mobiliário escolar em Caruaru e região PE. Atendemos creches, escolas públicas e privadas, universidades e institutos. Conjuntos aluno, professor, refeitório e mais.',
    keywords: 'mobiliário escolar Recife, carteiras escolares Petrolina, conjuntos escolares Garanhuns, licitação mobiliário escolar Arcoverde, mobiliário escolar Sertão PE, carteiras escolares Pernambuco, conjuntos infantis Agreste, mobiliário escolar Nordeste, carteiras universitárias Recife, mesa professor Petrolina, armário escolar Garanhuns, mobiliário escolar Caruaru, carteiras escolares Bezerros, mobiliário creche Arcoverde',
    path: '/escolar',
    ogTitle: 'Mobiliário Escolar Caruaru PE | Home Office Premium',
    ogDescription: 'Da creche à universidade: mobiliário escolar em Caruaru PE. Entrega e montagem na região.',
  });

  useEffect(() => {
    const schemaId = 'schema-escolar';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Mobiliário Escolar – Home Office Premium',
      description: 'Mobiliário escolar em Caruaru PE. Mesas, cadeiras, conjuntos infantis, refeitórios e auditórios.',
      url: `${siteUrl}/escolar`,
    });

    const itemListId = 'schema-escolar-itemlist';
    let itemListScript = document.getElementById(itemListId) as HTMLScriptElement | null;
    if (!itemListScript) { itemListScript = document.createElement('script'); itemListScript.id = itemListId; itemListScript.type = 'application/ld+json'; document.head.appendChild(itemListScript); }
    itemListScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Mobiliário Escolar – Home Office Premium Caruaru',
      url: `${siteUrl}/escolar`,
      numberOfItems: escolarProducts.length,
      itemListElement: escolarProducts.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.name,
          description: product.description,
          image: product.image,
          offers: {
            '@type': 'Offer',
            priceCurrency: 'BRL',
            price: '0',
            availability: 'https://schema.org/InStock',
            seller: { '@type': 'Organization', name: 'Home Office Premium' },
            url: `${siteUrl}/contato`,
            priceSpecification: {
              '@type': 'PriceSpecification',
              priceCurrency: 'BRL',
              description: 'Consulte o preço pelo WhatsApp ou formulário de contato',
            },
          },
        },
      })),
    });

    const faqId = 'schema-escolar-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Vocês atendem compras por licitação e pregão eletrônico?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim! Trabalhamos amplamente com processos licitatórios — pregão eletrônico, dispensa de licitação e ata de registro de preços. Emitimos nota fiscal, fornecemos toda a documentação exigida (certidões, declarações, especificações técnicas) e cumprimos os prazos de entrega contratual. Nossos produtos seguem as especificações do FNDE e normas ABNT para mobiliário escolar.',
          },
        },
        {
          '@type': 'Question',
          name: 'Os produtos seguem as normas técnicas para mobiliário escolar (ABNT/FNDE)?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim. Nossa linha escolar é desenvolvida conforme a NBR 14006 (mobiliário escolar) e os padrões do FNDE para aquisição de mobiliário com recursos do governo federal. Os conjuntos para alunos têm regulagem de altura, estrutura tubular reforçada e tampo em MDP com fórmica de alta resistência. Fornecemos laudos técnicos e memorial descritivo quando necessário para processos licitatórios.',
          },
        },
        {
          '@type': 'Question',
          name: 'Qual o prazo de entrega para escolas públicas de Caruaru e região?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Para clientes de Caruaru, o prazo médio é de 10 a 20 dias úteis após confirmação do pedido, dependendo do volume. Para cidades da região como Bezerros, Santa Cruz do Capibaribe, Garanhuns e Arcoverde, o prazo pode variar de 15 a 30 dias úteis. Em contratos com prazos definidos, trabalhamos para garantir a entrega dentro do cronograma acordado.',
          },
        },
        {
          '@type': 'Question',
          name: 'Vocês fazem a montagem do mobiliário na escola?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim, a montagem está inclusa no serviço. Nossa equipe própria faz a entrega e a montagem completa nas dependências da instituição, organizando os móveis conforme o layout da escola. Isso facilita muito para gestores que precisam receber grandes quantidades de móveis sem mobilizar a equipe interna.',
          },
        },
        {
          '@type': 'Question',
          name: 'É possível comprar conjuntos mistos para diferentes faixas etárias?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Com certeza! Atendemos toda a cadeia educacional — da creche à universidade. Podemos montar um pedido com conjuntos infantis (tamanhos menores, materiais atóxicos), conjuntos para ensino fundamental, médio e até cadeiras universitárias. Fazemos o dimensionamento por faixa etária e recomendamos o melhor modelo para cada espaço.',
          },
        },
        {
          '@type': 'Question',
          name: 'Os materiais da linha infantil são seguros para crianças pequenas?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim, a linha para creches e pré-escolas utiliza materiais 100% atóxicos, bordas arredondadas e acabamento resistente a impactos. As estruturas são baixas e proporcionais ao tamanho das crianças, garantindo segurança e ergonomia. Seguimos as recomendações da ABNT NBR 14006 para os diferentes tamanhos por faixa etária (tamanhos 1 a 6).',
          },
        },
        {
          '@type': 'Question',
          name: 'Atendem também secretarias, diretorias e salas de professores?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim! Além do mobiliário para salas de aula, equipamos também secretarias, diretorias, salas de reunião, salas de professores, bibliotecas e auditórios. Temos linha corporativa completa com mesas, cadeiras, armários, estantes e arquivos — tudo em um único fornecedor para simplificar o processo de compra.',
          },
        },
        {
          '@type': 'Question',
          name: 'Como funciona o processo de orçamento? É necessário ir até a loja?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Não precisa vir até a loja. Basta entrar em contato pelo WhatsApp ou formulário do site. Enviamos nosso catálogo completo com especificações técnicas, pedimos a quantidade e metragem dos ambientes e retornamos com orçamento detalhado em até 24 horas. Também realizamos visita técnica presencial para grandes projetos ou licitações, gratuitamente para Caruaru e região.',
          },
        },
      ],
    });

    return () => { script?.remove(); itemListScript?.remove(); faqScript?.remove(); };
  }, []);



  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 h-[520px] md:h-[620px] overflow-hidden">
        <img
          src="/media/ia/0230.webp"
          alt="Mobiliário escolar para salas de aula, refeitórios e auditórios em Caruaru PE"
          loading="eager"
          fetchPriority="high"
          decoding="sync"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-xl">
              {/* Breadcrumb visual */}
              <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-white/40 mb-5">
                <Link to="/" className="hover:text-white/80 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/produtos" className="hover:text-white/80 transition-colors">Produtos</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white/70 font-medium">Mobiliário Escolar</span>
              </nav>
              <div className="flex flex-wrap gap-2 mb-6">
                <div className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-xs font-semibold px-4 py-2 rounded-full uppercase tracking-widest">
                  <i className="ri-school-line text-xs"></i>
                  Mobiliário Escolar
                </div>
                <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full border border-white/30">
                  <i className="ri-map-pin-line text-xs"></i>
                  Caruaru – PE
                </div>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                Da Creche à<br />
                <span className="text-[#E8610A]">Universidade</span>
              </h1>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                A Home Office Premium está em Caruaru, PE, e fornece mobiliário escolar completo para instituições de ensino da cidade e de toda a região. Creches, escolas públicas e privadas, institutos e universidades.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#C4500A] transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-whatsapp-line"></i>
                  Solicitar Orçamento
                </a>
                <a href="#produtos" className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white text-sm font-medium px-6 py-3 rounded-full hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
                  Ver Produtos
                  <i className="ri-arrow-down-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Localização + diferenciais */}
      <section className="py-16 bg-[#fafaf8]">
        <div className="max-w-7xl mx-auto px-6">
          {/* Banner localização */}
          <div className="bg-[#E8610A] rounded-2xl px-8 py-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                <i className="ri-map-pin-2-fill text-white text-2xl"></i>
              </div>
              <div>
                <p className="text-white font-bold text-lg leading-tight">Estamos em Caruaru, Pernambuco</p>
                <p className="text-white/80 text-sm">Atendemos Caruaru e toda a região do Agreste — entrega e montagem incluídas</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 text-white/90 text-sm">
              {['Caruaru', 'Bezerros', 'Santa Cruz do Capibaribe', 'Garanhuns', 'Arcoverde', 'Todo o Agreste PE'].map(city => (
                <span key={city} className="flex items-center gap-1 bg-white/15 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap">
                  <i className="ri-checkbox-circle-line text-xs"></i>
                  {city}
                </span>
              ))}
            </div>
          </div>
          {/* Cards diferenciais */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-shield-check-line', title: 'Normas ABNT', desc: 'Produtos conforme NBR 14006 e padrões FNDE para mobiliário escolar' },
              { icon: 'ri-leaf-line', title: 'Material Atóxico', desc: 'Linha infantil com materiais 100% atóxicos e seguros para crianças' },
              { icon: 'ri-truck-line', title: 'Entrega em Caruaru', desc: 'Entregamos e montamos em Caruaru e região com equipe própria' },
              { icon: 'ri-file-list-3-line', title: 'Atende Licitações', desc: 'Nota fiscal, documentação completa e condições especiais para órgãos públicos' },
            ].map(item => (
              <div key={item.title} className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-xl mb-4">
                  <i className={`${item.icon} text-[#E8610A] text-lg`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-sm mb-2">{item.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Segmentos atendidos */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Quem Atendemos</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Soluções completas para toda instituição de ensino
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Atendemos desde pequenas creches até grandes universidades, com produtos que unem resistência, segurança e design. Trabalhamos com licitações públicas e contratos privados.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                Nossa equipe oferece consultoria completa: levantamento de necessidades, projeto de layout, fornecimento, entrega e montagem — tudo em um só lugar.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {segments.map(seg => (
                  <div key={seg.label} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#E8610A]/10 rounded-lg flex-shrink-0">
                      <i className={`${seg.icon} text-[#E8610A] text-sm`}></i>
                    </div>
                    <span className="text-gray-700 text-sm">{seg.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden h-96">
              <img
                src="/media/ia/0231.webp"
                alt="Sala de aula equipada"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A] rounded-xl flex-shrink-0">
                    <i className="ri-school-line text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-sm">Atendimento para Licitações</p>
                    <p className="text-gray-500 text-xs">Documentação completa e nota fiscal</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catálogo de produtos */}
      <section id="produtos" className="py-20 bg-[#fafaf8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Catálogo</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Linha Escolar</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">Clique em uma linha para ver os produtos. Todos com tampo em MDP, acabamento em fórmica e perfil em PVC.</p>
          </div>

          {/* Linhas como accordion */}
          <div className="space-y-3">
            {escolarLines.map(line => {
              const lineProducts = escolarProducts.filter(p => p.line === line.id);
              const isOpen = activeLine === line.id;
              return (
                <div key={line.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  {/* Header clicável */}
                  <button
                    onClick={() => setActiveLine(isOpen ? '' : line.id)}
                    className="w-full flex items-center justify-between px-5 py-3.5 text-left cursor-pointer hover:bg-[#fafaf8] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#E8610A]/10 rounded-lg flex-shrink-0">
                        <i className={`${line.icon} text-[#E8610A] text-sm`}></i>
                      </div>
                      <p className="text-gray-900 font-semibold text-sm">{line.label}</p>
                    </div>
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                      isOpen ? 'bg-[#E8610A] text-white rotate-180' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <i className="ri-arrow-down-s-line text-base"></i>
                    </div>
                  </button>

                  {/* Conteúdo expandido: fotos do catálogo */}
                  {isOpen && (
                    <div className="border-t border-gray-100">
                      {/* Fotos únicas por linha (as imagens já são a página do catálogo) */}
                      <div className="p-4">
                        {/* Imagem da página do catálogo em destaque */}
                        <div className="mb-4 rounded-xl overflow-hidden border border-gray-100">
                          <img
                            src={lineProducts[0]?.image}
                            alt={`Catálogo ${line.label}`}
                            className="w-full object-contain bg-white"
                            style={{ maxHeight: '420px' }}
                          />
                        </div>
                        {/* Tabela de modelos */}
                        <div className="overflow-x-auto mb-6">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-[#fafaf8]">
                                <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs uppercase tracking-wider rounded-l-lg">Código</th>
                                <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs uppercase tracking-wider">Produto</th>
                                <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs uppercase tracking-wider">Dimensões</th>
                                <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs uppercase tracking-wider">Cores</th>
                                <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs uppercase tracking-wider rounded-r-lg">Orçamento</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {lineProducts.map(product => {
                                const code = product.name.split('–')[1]?.trim() ?? product.name;
                                const productDims = (product as typeof product & { dimensions?: string }).dimensions;
                                return (
                                  <tr key={product.id} className="hover:bg-[#fafaf8] transition-colors">
                                    <td className="px-4 py-3">
                                      <span className="bg-[#E8610A]/10 text-[#E8610A] font-bold text-xs px-2 py-1 rounded-md">{code}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <p className="text-gray-800 font-medium text-sm">{product.description}</p>
                                      {product.badge && <span className="mt-1 inline-block bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">{product.badge}</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="text-gray-500 text-xs">{productDims ?? '—'}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex flex-wrap gap-1">
                                        {product.colors.map(c => (
                                          <span key={c} className="text-xs text-gray-500 bg-[#f5f5f0] px-2 py-0.5 rounded-full">{c}</span>
                                        ))}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3">
                                      <a
                                        href={`https://wa.me/5581982030285?text=Oi%2C%20tenho%20interesse%20no%20produto%3A%20${encodeURIComponent(product.name)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 bg-[#E8610A] text-white text-xs font-medium px-3 py-2 rounded-lg hover:bg-[#C4500A] transition-colors whitespace-nowrap cursor-pointer"
                                      >
                                        <i className="ri-whatsapp-line"></i>
                                        Pedir
                                      </a>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ambientes */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Ambientes</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Equipamos todos os espaços</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">Do portão de entrada ao auditório, temos o mobiliário certo para cada ambiente da sua instituição.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Sala de Aula', desc: 'Carteiras, cadeiras e mesas para todos os níveis de ensino.', image: '/media/ia/0232.webp', icon: 'ri-book-open-line' },
              { title: 'Refeitório Escolar', desc: 'Conjuntos resistentes e de fácil higienização para cantinas e refeitórios.', image: '/media/ia/0233.webp', icon: 'ri-restaurant-line' },
              { title: 'Espaço Infantil', desc: 'Conjuntos coloridos e seguros para creches e pré-escolas.', image: '/media/ia/0234.webp', icon: 'ri-bear-smile-line' },
              { title: 'Biblioteca', desc: 'Estantes, mesas de estudo e cadeiras para salas de leitura.', image: '/media/ia/0235.webp', icon: 'ri-booklet-line' },
              { title: 'Auditório', desc: 'Poltronas e cadeiras para eventos, palestras e formaturas.', image: '/media/ia/0236.webp', icon: 'ri-mic-line' },
              { title: 'Área Administrativa', desc: 'Mesas, cadeiras e armários para secretarias e diretorias.', image: '/media/ia/0237.webp', icon: 'ri-building-line' },
            ].map(env => (
              <div key={env.title} className="group relative rounded-2xl overflow-hidden h-56 cursor-pointer">
                <img src={env.image} alt={env.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-[#E8610A] rounded-lg">
                      <i className={`${env.icon} text-white text-xs`}></i>
                    </div>
                    <h3 className="text-white font-bold text-base">{env.title}</h3>
                  </div>
                  <p className="text-white/70 text-xs leading-relaxed">{env.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#fafaf8]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Dúvidas Frequentes</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Perguntas de Diretores e Secretários</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">Respondemos as principais dúvidas de gestores e secretarias de educação na hora de adquirir mobiliário escolar.</p>
          </div>
          <div className="space-y-3">
            {[
              {
                q: 'Vocês atendem compras por licitação e pregão eletrônico?',
                a: 'Sim! Trabalhamos amplamente com processos licitatórios — pregão eletrônico, dispensa de licitação e ata de registro de preços. Emitimos nota fiscal, fornecemos toda a documentação exigida (certidões, declarações, especificações técnicas) e cumprimos os prazos de entrega contratual. Nossos produtos seguem as especificações do FNDE e normas ABNT para mobiliário escolar.',
              },
              {
                q: 'Os produtos seguem as normas técnicas para mobiliário escolar (ABNT/FNDE)?',
                a: 'Sim. Nossa linha escolar é desenvolvida conforme a NBR 14006 (mobiliário escolar) e os padrões do FNDE para aquisição de mobiliário com recursos do governo federal. Os conjuntos para alunos têm regulagem de altura, estrutura tubular reforçada e tampo em MDP com fórmica de alta resistência. Fornecemos laudos técnicos e memorial descritivo quando necessário para processos licitatórios.',
              },
              {
                q: 'Qual o prazo de entrega para escolas públicas de Caruaru e região?',
                a: 'Para clientes de Caruaru, o prazo médio é de 10 a 20 dias úteis após confirmação do pedido, dependendo do volume. Para cidades da região como Bezerros, Santa Cruz do Capibaribe, Garanhuns e Arcoverde, o prazo pode variar de 15 a 30 dias úteis. Em contratos com prazos definidos, trabalhamos para garantir a entrega dentro do cronograma acordado.',
              },
              {
                q: 'Vocês fazem a montagem do mobiliário na escola?',
                a: 'Sim, a montagem está inclusa no serviço. Nossa equipe própria faz a entrega e a montagem completa nas dependências da instituição, organizando os móveis conforme o layout da escola. Isso facilita muito para gestores que precisam receber grandes quantidades de móveis sem mobilizar a equipe interna.',
              },
              {
                q: 'É possível comprar conjuntos mistos para diferentes faixas etárias?',
                a: 'Com certeza! Atendemos toda a cadeia educacional — da creche à universidade. Podemos montar um pedido com conjuntos infantis (tamanhos menores, materiais atóxicos), conjuntos para ensino fundamental, médio e até cadeiras universitárias. Fazemos o dimensionamento por faixa etária e recomendamos o melhor modelo para cada espaço.',
              },
              {
                q: 'Os materiais da linha infantil são seguros para crianças pequenas?',
                a: 'Sim, a linha para creches e pré-escolas utiliza materiais 100% atóxicos, bordas arredondadas e acabamento resistente a impactos. As estruturas são baixas e proporcionais ao tamanho das crianças, garantindo segurança e ergonomia. Seguimos as recomendações da ABNT NBR 14006 para os diferentes tamanhos por faixa etária (tamanhos 1 a 6).',
              },
              {
                q: 'Atendem também secretarias, diretorias e salas de professores?',
                a: 'Sim! Além do mobiliário para salas de aula, equipamos também secretarias, diretorias, salas de reunião, salas de professores, bibliotecas e auditórios. Temos linha corporativa completa com mesas, cadeiras, armários, estantes e arquivos — tudo em um único fornecedor para simplificar o processo de compra.',
              },
              {
                q: 'Como funciona o processo de orçamento? É necessário ir até a loja?',
                a: 'Não precisa vir até a loja. Basta entrar em contato pelo WhatsApp ou formulário do site. Enviamos nosso catálogo completo com especificações técnicas, pedimos a quantidade e metragem dos ambientes e retornamos com orçamento detalhado em até 24 horas. Também realizamos visita técnica presencial para grandes projetos ou licitações, gratuitamente para Caruaru e região.',
              },
            ].map((item, i) => (
              <FaqItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[#E8610A]/20 text-[#E8610A] text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
            <i className="ri-map-pin-line"></i>
            Caruaru, Pernambuco
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Sua escola em Caruaru merece<br className="hidden md:block" /> o melhor mobiliário
          </h2>
          <p className="text-gray-400 text-sm mb-3 max-w-xl mx-auto">
            Somos de Caruaru e atendemos escolas, creches, universidades e órgãos públicos de toda a região do Agreste PE. Orçamento rápido, entrega local e montagem incluída.
          </p>
          <p className="text-gray-500 text-xs mb-10">
            <i className="ri-map-pin-2-line mr-1"></i>
            Atendemos: Caruaru · Bezerros · Santa Cruz do Capibaribe · Garanhuns · Arcoverde · e toda a região
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20de%20Caruaru%20e%20preciso%20de%20mobili%C3%A1rio%20escolar%2C%20pode%20me%20ajudar%3F" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-[#C4500A] transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line"></i>
              Pedir Orçamento pelo WhatsApp
            </a>
            <Link to="/contato" className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-mail-line"></i>
              Enviar Mensagem
            </Link>
          </div>
        </div>
      </section>

      {/* Modal de detalhes do produto */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setSelectedProduct(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-2xl w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="relative h-72 bg-[#f8f8f6]">
              <img
                src={selectedProduct.image}
                alt={selectedProduct.name}
                className="w-full h-full object-cover object-top"
              />
              {selectedProduct.badge && (
                <div className="absolute top-4 left-4">
                  <span className="bg-[#E8610A] text-white text-xs font-semibold px-3 py-1.5 rounded-full">{selectedProduct.badge}</span>
                </div>
              )}
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/90 rounded-full hover:bg-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-700 text-lg"></i>
              </button>
            </div>
            <div className="p-8">
              <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-wider mb-2">Mobiliário Escolar</p>
              <h2 className="text-gray-900 font-bold text-xl mb-3">{selectedProduct.name}</h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">{selectedProduct.description}</p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#fafaf8] rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Material</p>
                  <p className="text-gray-800 font-semibold text-sm">{selectedProduct.material}</p>
                </div>
                {'dimensions' in selectedProduct && (selectedProduct as typeof selectedProduct & { dimensions?: string }).dimensions && (
                  <div className="bg-[#fafaf8] rounded-xl p-4">
                    <p className="text-gray-400 text-xs mb-1">Dimensões (LxPxA)</p>
                    <p className="text-gray-800 font-semibold text-sm">{(selectedProduct as typeof selectedProduct & { dimensions?: string }).dimensions}</p>
                  </div>
                )}
                <div className="bg-[#fafaf8] rounded-xl p-4 col-span-2">
                  <p className="text-gray-400 text-xs mb-2">Cores disponíveis</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedProduct.colors.map(c => (
                      <span key={c} className="text-xs text-gray-600 bg-white border border-gray-200 px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                  </div>
                </div>
              </div>
              <a
                href={`https://wa.me/5581982030285?text=Oi%2C%20tenho%20interesse%20no%20produto%3A%20${encodeURIComponent(selectedProduct.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold py-3.5 rounded-xl hover:bg-[#C4500A] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                Solicitar Orçamento para {selectedProduct.name}
              </a>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <a
        href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B50es"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200"
        aria-label="Falar com Home Office Premium pelo WhatsApp"
      >
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
